'use client'

import React, { ReactElement, useRef, useState } from 'react'
import { Sizes } from '@/components/common/picture/Sizes'
import { Picture, PictureSizeDefinition } from '@/components/common/picture/Picture'
import classNames from 'classnames'
import { DiscountLabel, DiscountLabelAlignment } from '@/components/elements/DiscountLabel'
import { LikeHotelButton } from '@/components/elements/hotel_tease_element/LikeHotelButton'
import ImagesIcon from '@/icons/font-awesome/sharp-solid/images.svg'
import { PictureWallBackButton } from '@/components/hotel/PictureWallBackButton'
import Skeleton from 'react-loading-skeleton'
import { ShareButton } from '../elements/hotel_tease_element/ShareButton'
import PictureWall from '@/components/hotel/PictureWall'
import { Currency } from '@/types/Page'

// const Lar
const LARGE_IMAGE_SIZES: PictureSizeDefinition = {
  [Sizes.xs]: [280, 599, 384],
  [Sizes.sm]: [600, 727, 384],
  [Sizes.md]: [356, 484, 384],
  [Sizes.lg]: [484, 612, 384],
  [Sizes.xl]: [612, 612, 384],
}

const SMALL_IMAGE_SIZES: PictureSizeDefinition = {
  [Sizes.xs]: [1, 1, 1],
  [Sizes.sm]: [170, 234, 184],
  [Sizes.md]: [170, 234, 184],
  [Sizes.lg]: [234, 298, 184],
  [Sizes.xl]: [298, 298, 184],
}

interface HotelImagesParams {
  currency: Currency
  images: string[]
  hotel: string
  photosButtonLabel: string
  closePictureWallButtonLabel: string
  discount: string | undefined
}

export function HotelImages(props: HotelImagesParams): ReactElement {
  const [showAllImages, setShowAllImages] = useState(false)
  const [scrollDestination, setScrollDestination] = useState<number | null>(null)

  function showPictureWall(index: number): void {
    setShowAllImages(true)
    setScrollDestination(index)
  }

  function closePictureWall(): void {
    setShowAllImages(false)
    setScrollDestination(null)
  }

  return (
    <>
      <div className="relative grid h-72 w-full grid-rows-2 gap-1 overflow-hidden rounded-md sm:grid-cols-3 md:h-96 md:grid-cols-4">
        <div className="absolute right-2 top-2 z-10 flex">
          <LikeHotelButton
            size="h-8 w-8"
            hotelCode={props.hotel}
            colorUnselected="rgba(255, 0, 0, 0)"
            strokeUnselected="white"
            strokeSelected="white"
            greyBackground={true}
          />
          <ShareButton
            additionalClassNames={'web-share absolute right-10 top-1.5 z-10 flex'}
            imageUrl={props.images[0]}
          />
        </div>
        {props.images.map((image, index) => (
          <div
            key={image}
            className={classNames('relative ', {
              'col-span-4 row-span-4 block sm:col-span-2 sm:row-span-2': index === 0,
              'hidden md:col-span-1 md:row-span-1 md:block': index !== 0,
              'hidden sm:block': index == 1 || index == 2,
            })}
            onClick={() => showPictureWall(index)}
          >
            {index === 0 && props.discount && (
              <DiscountLabel
                discount={props.discount}
                alignment={DiscountLabelAlignment.Left}
                large={true}
              />
            )}

            <Picture
              cornerRounding={'rounded-none'}
              src={image}
              additionalClassNames={classNames(
                'h-full max-h-full w-full max-w-full cursor-pointer object-cover',
              )}
              priority={true}
              alt={''}
              size={index === 0 ? LARGE_IMAGE_SIZES : SMALL_IMAGE_SIZES}
              staticPosition={true}
            />
          </div>
        ))}

        <button
          className="absolute bottom-4 right-4 flex items-center gap-2 rounded-md border border-white bg-stone-800/50 px-3 py-1 font-bold"
          onClick={(event) => {
            setShowAllImages(true)
            event.stopPropagation()
          }}
        >
          <ImagesIcon className="h-4 w-4 fill-white" />
          <span className="block  whitespace-nowrap text-sm text-white">
            {props.photosButtonLabel}
          </span>
        </button>
      </div>
      {showAllImages && (
        <RealPictureWall
          closePictureWallButtonLabel={props.closePictureWallButtonLabel}
          hotel={props.hotel}
          closePictureWall={closePictureWall}
          scrollDestination={scrollDestination}
          currency={props.currency}
        />
      )}
    </>
  )
}

interface RealPictureWallProps {
  currency: Currency
  hotel: string
  closePictureWall: () => void
  closePictureWallButtonLabel: string
  scrollDestination: number | null
}

function RealPictureWall(props: RealPictureWallProps): ReactElement {
  const backButtonCallbackRef = useRef<() => void>()
  const [imagesLoaded, setImagesLoaded] = useState(false)

  return (
    <div className="fixed left-0 right-0 top-0 z-[2001] h-screen w-screen gap-4 overflow-y-scroll bg-white pt-12">
      <PictureWallBackButton
        onClick={() => backButtonCallbackRef.current?.()}
        closePictureWallButtonLabel={props.closePictureWallButtonLabel}
      />
      {!imagesLoaded && (
        <div className="z-10 w-full bg-white">
          <div
            className="mx-auto mt-6 grid max-w-screen-lg grid-cols-3 grid-rows-[repeat(12,_400px_220px)] gap-4 px-7 tiny:grid-rows-[400px_220px_400px_220px_400px_220px] md:px-4"
            id="gallery"
          >
            <div className="col-span-3 rounded-lg">
              <Skeleton className="h-full" />
            </div>
            <div className="col-span-3 rounded-lg tiny:col-span-1">
              <Skeleton className="h-full" />
            </div>
            <div className="col-span-3 rounded-lg tiny:col-span-1">
              <Skeleton className="h-full" />
            </div>
            <div className="col-span-3 rounded-lg tiny:col-span-1">
              <Skeleton className="h-full" />
            </div>
            <div className="col-span-3 rounded-lg">
              <Skeleton className="h-full" />
            </div>
            <div className="col-span-3 rounded-lg tiny:col-span-1">
              <Skeleton className="h-full" />
            </div>
            <div className="col-span-3 rounded-lg tiny:col-span-1">
              <Skeleton className="h-full" />
            </div>
            <div className="col-span-3 rounded-lg tiny:col-span-1">
              <Skeleton className="h-full" />
            </div>
            <div className="col-span-3 rounded-lg">
              <Skeleton className="h-full" />
            </div>
            <div className="col-span-3 rounded-lg tiny:col-span-1">
              <Skeleton className="h-full" />
            </div>
            <div className="col-span-3 rounded-lg tiny:col-span-1">
              <Skeleton className="h-full" />
            </div>
            <div className="col-span-3 rounded-lg tiny:col-span-1">
              <Skeleton className="h-full" />
            </div>
          </div>
        </div>
      )}
      <PictureWall
        closeButtonCallbackRef={backButtonCallbackRef}
        imagesLoaded={imagesLoaded}
        imagesLoadedCallback={() => setImagesLoaded(true)}
        {...props}
        currency={props.currency}
      />
    </div>
  )
}
